import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { getAppSettings, updateAppSettings } from '../../api/admin/AppSettings'
import handleToast from '../toast/handleToast'

interface AdminForceUpdateProps {
  isOpen: boolean
  onClose: () => void
}

const AdminForceUpdate: React.FC<AdminForceUpdateProps> = ({ isOpen, onClose }) => {
  const [version, setVersion] = useState<string>('')
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data: appSettings, isLoading: isLoadingSettings } = useQuery(['getAppSettings'], getAppSettings, {
    enabled: isOpen,
    onSuccess: (data) => {
      if (data?.minimum_app_version) {
        setVersion(data.minimum_app_version)
      }
    },
  })

  const updateSettings = useMutation(['updateAppSettings'], updateAppSettings, {
    onMutate: async (newData) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries(['getAppSettings'])

      // Snapshot the previous value
      const previousSettings = queryClient.getQueryData(['getAppSettings'])

      // Optimistically update to the new value
      queryClient.setQueryData(['getAppSettings'], (old: any) => ({
        ...old,
        minimum_app_version: newData.minimum_app_version,
      }))

      // Return a context object with the snapshotted value
      return { previousSettings }
    },
    onError: (_err, _newData, context: any) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(['getAppSettings'], context?.previousSettings)
      handleToast(toast, 'There was an error while updating the minimum app version')
    },
    onSuccess: () => {
      handleToast(toast, 'Minimum app version updated successfully')
      onClose()
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries(['getAppSettings'])
    },
  })

  const handleSubmit = () => {
    if (!version || version.trim() === '') {
      handleToast(toast, 'Please enter a valid version number')
      return
    }

    // Basic version format validation (e.g., "1.2.3" or "1.3")
    const versionPattern = /^\d+(\.\d+)*$/
    if (!versionPattern.test(version)) {
      handleToast(toast, 'Please enter a valid version format (e.g., 1.2.3 or 1.3)')
      return
    }

    updateSettings.mutate({ minimum_app_version: version })
  }

  // Reset version when modal closes
  useEffect(() => {
    if (!isOpen && appSettings?.minimum_app_version) {
      setVersion(appSettings.minimum_app_version)
    }
  }, [isOpen, appSettings])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Force Update Settings</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb="20px">
            <FormLabel mb="8px">Current Minimum App Version</FormLabel>
            {isLoadingSettings ? (
              <Text variant="subtle" fontSize="sm">
                Loading...
              </Text>
            ) : (
              <Text variant="subtle" fontSize="md" fontWeight="500">
                {appSettings?.minimum_app_version || 'Not set'}
              </Text>
            )}
          </FormControl>

          <FormControl>
            <FormLabel mb="8px">New Minimum App Version</FormLabel>
            <Input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="e.g., 1.2.3 or 1.3"
              mb="10px"
            />
            <Text variant="subtle" fontSize="xs" color="gray.500">
              Users with versions below this will be required to update
            </Text>
          </FormControl>
        </ModalBody>
        <ModalFooter gap="10px">
          <Button variant="light" onClick={onClose} isDisabled={updateSettings.isLoading}>
            Cancel
          </Button>
          <Button
            variant="heavy"
            onClick={handleSubmit}
            isLoading={updateSettings.isLoading}
            isDisabled={!version || version.trim() === ''}
          >
            Update
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default AdminForceUpdate
